import { useState, useEffect, useRef } from 'react';
import { X, Copy, Check, FileDown, ChevronDown } from 'lucide-react';
import useTranslation from '@/hooks/useTranslation';
import { downloadBlob } from '@/utils/downloadFile';
import useCopyState from './useCopyState';
import type { ResearchArtifact } from '@/reactQuery/researchAssistant';
import ArtifactQuoteCard from './ArtifactQuoteCard';
import ArtifactDataTable from './ArtifactDataTable';
import ArtifactThemeSummary from './ArtifactThemeSummary';
import ArtifactChartBlock from './ArtifactChartBlock';
import ArtifactMarkdownReport from './ArtifactMarkdownReport';
import ArtifactWordCloud from './ArtifactWordCloud';
import ConfidenceTierBadge from './ConfidenceTierBadge';
import { useRespondentMap } from './useRespondentMap';

interface ArtifactPanelProps {
  artifacts: ResearchArtifact[];
  onClose: () => void;
  /** Called when the user clicks "Recompute" on a legacy tier-unknown badge. */
  onRecomputeTier?: (comparisonId: string) => void;
  /** True while the recompute mutation is in-flight for the active artifact. */
  isRecomputingTier?: boolean;
  /**
   * Project id, used to fetch the project-level respondent_index →
   * conversation_id lookup so `[R12]` citation pills inside artifact
   * bodies are clickable even when the thread's only artifact is a
   * theme_summary or markdown_report (i.e. no quote_collection that
   * already carries conversation_ids).
   */
  projectId?: string;
}

function ArtifactPanel({
  artifacts,
  onClose,
  onRecomputeTier,
  isRecomputingTier = false,
  projectId,
}: ArtifactPanelProps) {
  const { lang } = useTranslation();
  // Initial index defaults to 0 if there are artifacts, otherwise 0 (safe).
  // Math.max prevents -1 when artifacts.length === 0 (we early-return below
  // but useState fires before the early return).
  const [activeIdx, setActiveIdx] = useState(() => Math.max(0, artifacts.length - 1));
  const [copied, triggerCopied] = useCopyState();
  const prevLengthRef = useRef(artifacts.length);

  // Capture the element that had focus when the panel mounted so we can
  // restore focus when it closes — important for keyboard users who would
  // otherwise be dropped on `<body>`.
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  // Sprint A audit fix (H-5): move focus INTO the panel when it first
  // mounts so screen reader and keyboard users know it has opened.
  // Previously focus stayed in the chat textarea and the panel appeared
  // silently. The close button is the safest landing target — it's
  // always visible, doesn't trigger any data action on focus, and
  // becomes the start of the natural Tab order through the panel.
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    previouslyFocusedRef.current = document.activeElement as HTMLElement;
    // Defer the focus until after the panel has rendered so the element
    // is in the DOM. requestAnimationFrame is enough — no need for
    // setTimeout.
    requestAnimationFrame(() => {
      closeButtonRef.current?.focus();
    });
    return () => {
      // Restore focus on unmount if the previously-focused element is
      // still in the document
      const prev = previouslyFocusedRef.current;
      if (prev && document.body.contains(prev)) {
        try {
          prev.focus();
        } catch {
          // ignore focus failures
        }
      }
    };
  }, []);

  // Auto-switch to the newest artifact when one is added (e.g., during streaming).
  // We only follow growth — if the user manually switches tabs, that selection
  // is preserved until a new artifact arrives.
  useEffect(() => {
    if (artifacts.length > prevLengthRef.current) {
      setActiveIdx(artifacts.length - 1);
    }
    prevLengthRef.current = artifacts.length;
  }, [artifacts.length]);

  // Build the respondent → conversation lookup ONCE from the thread's
  // artifacts and pass it down to the artifact renderers that carry
  // `[Rn]` citation tokens in their body content. This makes `[R12]`
  // tokens render as the same clickable pill component in the artifact
  // panel as in the chat bubble — previously the artifact rendered
  // body content as plain text and left `[R12]` as raw bracket text.
  // Computed BEFORE the early-return below — Hooks must run in the
  // same order on every render, so the early return cannot come first.
  const respondentMap = useRespondentMap(artifacts, projectId);

  if (artifacts.length === 0) return null;

  // Clamp activeIdx to the valid range — guards against the user removing
  // artifacts (not currently possible, but defensive).
  const clampedIdx = Math.max(0, Math.min(activeIdx, artifacts.length - 1));
  const active = artifacts[clampedIdx];

  /**
   * Copy the active artifact as human-readable formatted text.
   * Each artifact type renders to a researcher-friendly format suitable
   * for pasting into documents, emails, or presentations.
   */
  const handleCopyAll = () => {
    if (!active) return;
    let text = `${active.title}\n\n`;
    switch (active.type) {
      case 'quote_collection':
        text += (
          (active.data?.quotes || []) as Array<{
            text: string;
            respondent_index: number;
            respondent_metadata?: Record<string, unknown>;
          }>
        )
          .map(
            (q) =>
              `"${q.text}" — Respondent ${q.respondent_index}${
                q.respondent_metadata
                  ? `, ${Object.values(q.respondent_metadata).filter(Boolean).join(', ')}`
                  : ''
              }`,
          )
          .join('\n\n');
        break;
      case 'data_table': {
        const cols = (active.data?.columns || []) as Array<{ label: string; key: string }>;
        const rows = (active.data?.rows || []) as Array<Record<string, unknown>>;
        text += `${cols.map((c) => c.label).join('\t')}\n`;
        text += rows.map((row) => cols.map((c) => row[c.key] ?? '').join('\t')).join('\n');
        break;
      }
      case 'theme_summary':
        text += ((active.data?.themes || []) as Array<{ title: string; body: string }>)
          .map((t) => `${t.title}\n${t.body}`)
          .join('\n\n');
        break;
      case 'bar_chart':
        text += ((active.data?.series || []) as Array<{ name: string; value: unknown }>)
          .map((s) => `${s.name}: ${s.value}`)
          .join('\n');
        break;
      case 'markdown_report':
        text += active.data?.content || '';
        break;
      case 'word_cloud': {
        // TELV1-332 reframe: data.concepts replaces the old data.words shape.
        const concepts = (active.data?.concepts || []) as Array<{
          phrase: string;
          unique_respondents: number;
          sentiment: string;
        }>;
        text += 'phrase\tunique_respondents\tsentiment\n';
        text += concepts
          .map((c) => `${c.phrase}\t${c.unique_respondents}\t${c.sentiment}`)
          .join('\n');
        break;
      }
      default:
        text += JSON.stringify(active.data, null, 2);
    }
    navigator.clipboard
      .writeText(text)
      .then(() => triggerCopied())
      .catch(() => {});
  };

  // Plain function (not useCallback) to keep the hook ordering consistent
  // with the early-return at line 68. handleCopyAll above follows the same
  // pattern. Memoization here is not worth the rules-of-hooks risk: this
  // function is only used in a single button onClick and the parent
  // component re-renders rarely.
  const handleExportSession = () => {
    // Escape pipe characters in markdown table cells so a quote like "good | bad"
    // doesn't break the table structure.
    const escapeCell = (v: unknown): string =>
      String(v ?? '')
        .replace(/\|/g, '\\|')
        .replace(/\n/g, ' ');

    const sections = artifacts.map((a) => {
      let content = '';
      switch (a.type) {
        case 'quote_collection':
          content = ((a.data?.quotes || []) as Array<{ text: string; respondent_index: number }>)
            .map((q) => `> "${q.text}" — Respondent ${q.respondent_index}`)
            .join('\n\n');
          break;
        case 'data_table': {
          const cols = (a.data?.columns || []) as Array<{ label: string; key: string }>;
          const rows = (a.data?.rows || []) as Array<Record<string, unknown>>;
          content = [
            cols.map((c) => escapeCell(c.label)).join(' | '),
            cols.map(() => '---').join(' | '),
            ...rows.map((row) => cols.map((c) => escapeCell(row[c.key])).join(' | ')),
          ].join('\n');
          break;
        }
        case 'theme_summary':
          content = ((a.data?.themes || []) as Array<{ title: string; body: string }>)
            .map((t) => `### ${t.title}\n\n${t.body}`)
            .join('\n\n');
          break;
        case 'bar_chart':
          content = ((a.data?.series || []) as Array<{ name: string; value: unknown }>)
            .map((s) => `- ${s.name}: ${s.value}`)
            .join('\n');
          break;
        case 'markdown_report':
          content = a.data?.content || '';
          break;
        case 'word_cloud': {
          // TELV1-332 reframe: data.concepts replaces the old data.words shape.
          const concepts = (a.data?.concepts || []) as Array<{
            phrase: string;
            unique_respondents: number;
            sentiment: string;
          }>;
          content = concepts
            .map((c) => `- ${c.phrase} — ${c.unique_respondents} respondents (${c.sentiment})`)
            .join('\n');
          break;
        }
        default:
          content = JSON.stringify(a.data, null, 2);
      }
      return `## ${a.title}\n\n${content}`;
    });

    const markdown = `# Research Session Export\n\n${sections.join('\n\n---\n\n')}`;
    const blob = new Blob([markdown], { type: 'text/markdown' });
    downloadBlob(blob, `research-session-${new Date().toISOString().slice(0, 10)}.md`);
  };

  const renderArtifact = (artifact: ResearchArtifact) => {
    switch (artifact.type) {
      case 'quote_collection':
        return <ArtifactQuoteCard title={artifact.title} data={artifact.data} />;
      case 'data_table':
        return <ArtifactDataTable title={artifact.title} data={artifact.data} />;
      case 'theme_summary':
        return (
          <ArtifactThemeSummary
            title={artifact.title}
            data={artifact.data}
            respondentMap={respondentMap}
          />
        );
      case 'bar_chart':
        return <ArtifactChartBlock title={artifact.title} data={artifact.data} />;
      case 'word_cloud':
        return <ArtifactWordCloud title={artifact.title} data={artifact.data} />;
      case 'markdown_report':
        return (
          <ArtifactMarkdownReport
            title={artifact.title}
            data={artifact.data}
            respondentMap={respondentMap}
          />
        );
      default:
        return (
          <pre className="text-xs text-neutral-600 bg-neutral-50 p-3 rounded-lg overflow-auto">
            {JSON.stringify(artifact.data, null, 2)}
          </pre>
        );
    }
  };

  return (
    <div className="flex flex-col h-full border-l border-neutral-200 bg-white">
      {/* Header. Fixed h-14 (56px) so the three Research Assistant
          columns (threads, chat, artifacts) have aligned header bars. */}
      <div className="h-14 flex items-center justify-between px-4 border-b border-neutral-200 shrink-0">
        <span className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">
          {lang.get('msg.raArtifacts')}
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={handleExportSession}
            className="p-2 text-neutral-500 hover:text-neutral-700 rounded hover:bg-neutral-100 transition-colors min-h-[28px] min-w-[28px] flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
            title={lang.get('msg.exportAllArtifacts')}
            aria-label={lang.get('msg.exportAllArtifacts')}
          >
            <FileDown className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={handleCopyAll}
            className="p-2 text-neutral-500 hover:text-neutral-700 rounded hover:bg-neutral-100 transition-colors min-h-[28px] min-w-[28px] flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
            title={copied ? lang.get('msg.copied') : lang.get('msg.copyArtifact')}
            aria-label={copied ? lang.get('msg.copied') : lang.get('msg.copyArtifact')}
          >
            {copied ? (
              <Check className="h-3.5 w-3.5 text-primary" />
            ) : (
              <Copy className="h-3.5 w-3.5" />
            )}
          </button>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            aria-label={lang.get('msg.close')}
            className="p-2 text-neutral-500 hover:text-neutral-700 rounded hover:bg-neutral-100 transition-colors min-h-[28px] min-w-[28px] flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Confidence tier badge — shown above the artifact selector when the
          active artifact is sourced from a comparison. Three colour states for
          'directional' | 'indicative' | 'established'; grey "Tier unknown" for
          legacy docs. Per A.Q4: RA chat uses hard suppression for directional. */}
      {active && (active.confidence_tier !== undefined || active.comparison_id) && (
        <div className="px-3 py-1.5 border-b border-neutral-100 shrink-0">
          <ConfidenceTierBadge
            tier={active.confidence_tier}
            sampleSizes={
              active.sample_size_a !== undefined && active.sample_size_b !== undefined
                ? { a: active.sample_size_a, b: active.sample_size_b }
                : undefined
            }
            onRecompute={
              active.confidence_tier === undefined && active.comparison_id && onRecomputeTier
                ? () => onRecomputeTier(active.comparison_id!)
                : undefined
            }
            isRecomputing={active.confidence_tier === undefined && isRecomputingTier}
          />
        </div>
      )}

      {/* Artifact selector — dropdown scales better than tabs at 5+ artifacts.
          Shows the active artifact title with a chevron; clicking opens a
          list of all artifacts with full titles. With 1 artifact, no
          selector needed. */}
      {artifacts.length > 1 && (
        <div className="relative px-3 py-1.5 border-b border-neutral-100 shrink-0">
          <select
            value={clampedIdx}
            onChange={(e) => setActiveIdx(Number(e.target.value))}
            aria-label={lang.get('msg.raArtifacts')}
            className="w-full text-[12px] font-medium text-neutral-700 bg-neutral-50 border border-neutral-200 rounded-md pl-2.5 pr-7 py-1.5 appearance-none cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 truncate"
          >
            {artifacts.map((artifact, idx) => (
              <option key={artifact.artifact_id} value={idx}>
                {artifact.title}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-neutral-400 pointer-events-none" />
        </div>
      )}

      {/* Content */}
      <div
        role="tabpanel"
        id={active ? `artifact-panel-${active.artifact_id}` : undefined}
        aria-labelledby={active ? `artifact-tab-${active.artifact_id}` : undefined}
        className="flex-1 overflow-y-auto px-4 py-4"
      >
        {active?.source_question && (
          <p className="text-[11px] text-neutral-500 mb-3 italic">
            {lang.get('msg.basedOnQuestion')}: {active.source_question}
          </p>
        )}
        {active && active.confidence_tier === 'directional' ? (
          /* A.Q4 hard suppression: directional comparisons are hidden behind an
             expander in the RA chat. The user must explicitly opt in. This is
             asymmetric from the Compare tab (soft amber warning) — intentional. */
          <details className="group" data-testid="directional-expander">
            <summary className="cursor-pointer text-sm text-neutral-500 list-none flex items-center gap-2 py-2 hover:text-neutral-700">
              <span className="h-4 w-4 rounded-full bg-red-100 border border-red-200 shrink-0 flex items-center justify-center">
                <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
              </span>
              Show small-sample analysis anyway (min n=
              {active.sample_size_a !== undefined && active.sample_size_b !== undefined
                ? Math.min(active.sample_size_a, active.sample_size_b)
                : '?'}
              , methodologically directional)
            </summary>
            <div className="pt-2" data-testid="directional-body">
              {renderArtifact(active)}
            </div>
          </details>
        ) : (
          active && renderArtifact(active)
        )}
      </div>
    </div>
  );
}

export default ArtifactPanel;
